import { Box, Typography } from '@mui/material';
import { Base } from 'components/Containers/BaseContainer';
import useAppTheme from 'hooks/useAppTheme';
import { ThemePalette } from 'mui.theme';
import { useEffect, useMemo, useState } from 'react';
import {
  aggregateByAssetId,
  aggregateSubcomponentData,
  getLineChartData,
  getMaxAssetAndIndexForComponent,
} from '../utils';
import { ComponentsLineChart } from './LineChart';
import { ComponentsPieChart } from './PieChart';
import { ComponentsTreeChart } from './TreeChart';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const DistributionComp = ({
  selectedSubComponentData,
  assetsData,
  selectedSubComponent,
}: {
  selectedSubComponentData: any[];
  assetsData: any[];
  selectedSubComponent: string;
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const [isLoading, setIsLoading] = useState(true);

  const [selectedAssetsData, setSelectedAssetsData] = useState<{
    assets: string[];
    index: number[];
  }>({
    assets: [],
    index: [],
  });

  const handlePieClick = (data: any, index: number) => {
    const name = data?.name;
    if (name) {
      setSelectedAssetsData(() => ({
        assets: [name],
        index: [index],
      }));
    }
  };

  useEffect(() => {
    if (!assetsData || assetsData.length === 0) return;

    const maxAsset = getMaxAssetAndIndexForComponent(
      assetsData,
      'total_asset_cost'
    );

    if (maxAsset?.maxData?.name) {
      setSelectedAssetsData({
        assets: [maxAsset.maxData.name],
        index: [maxAsset.maxIndex],
      });
    }
    setIsLoading(false);
  }, [assetsData]);

  // lineChartData
  const lineChartData = useMemo(() => {
    const temp = getLineChartData(
      assetsData,
      selectedSubComponentData,
      selectedAssetsData?.assets
    );
    return temp;
  }, [assetsData, selectedSubComponentData, selectedAssetsData?.assets]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="400px"
        width={'100%'}
      >
        <Typography
          sx={{
            fontSize: '20px',
            color: palette.textPrimary,
          }}
        >
          {t('cdp_loading')}
        </Typography>
      </Box>
    );
  }
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)', // 5 equal-width columns
        gap: '20px',
        padding: '20px',
        width: '100%',
        boxSizing: 'border-box',
        backgroundColor: enableDarkTheme
          ? palette.toolBarBackground
          : palette.toolBarBackground,
        borderRadius: '8px',
        // height: '550px',
      }}
    >
      <div
        style={{
          gridColumn: 'span 2',
          width: '100%',
        }}
      >
        <Base
          sx={{
            height: '100%',
            maxHeight: '100%',
            backgroundColor: enableDarkTheme
              ? palette.boxBackground
              : palette.boxBackground,
            margin: 0,
          }}
        >
          <Box sx={{ textAlign: 'center' }} paddingTop={2}>
            <Typography
              sx={{
                color: palette.textPrimary,
              }}
            >
              {t('asset_distribution_by_total_cost_for')}
            </Typography>
            <Typography color={'#8884d8'} fontWeight={600}>
              {selectedSubComponent}
            </Typography>
          </Box>

          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              width: '100%',
              justifyContent: 'center',
              height: '400px',
            }}
          >
            <ComponentsPieChart
              data={assetsData}
              handlePieClick={handlePieClick}
              enableDarkTheme={enableDarkTheme}
              onlyOneActive
              activeIndices={selectedAssetsData?.index}
            />
          </Box>
        </Base>
      </div>
      <div style={{ gridColumn: 'span 3', width: '100%' }}>
        <Base
          sx={{
            height: '100%',
            maxHeight: '100%',
            backgroundColor: enableDarkTheme
              ? palette.boxBackground
              : palette.boxBackground,
            margin: 0,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            paddingTop={2}
          >
            <Typography
              sx={{
                color: palette.textPrimary,
              }}
            >
              {t('total_cost_distribution_for_nbsp')}
            </Typography>
            <Typography color="#8884d8" fontWeight={600}>
              {selectedSubComponent}
            </Typography>
            <Typography
              sx={{
                color: palette.textPrimary,
              }}
            >
              {t('nbsp_of_asset_nbsp')}
            </Typography>
            <Typography color="#8884d8" fontWeight={600}>
              {selectedAssetsData?.assets?.join(',')}
            </Typography>
          </Box>

          {lineChartData.length === 0 ? (
            <Box
              display={'flex'}
              width={'100%'}
              height={'100%'}
              justifyContent={'center'}
              alignItems={'center'}
            >
              <Typography
                sx={{
                  fontSize: '20px',
                  color: palette.textPrimary,
                }}
              >
                {t('no_line_chart_data')}
              </Typography>
            </Box>
          ) : (
            <Box width={'100%'} height={'420px'}>
              <ComponentsLineChart data={lineChartData} height="100%" />
            </Box>
          )}
        </Base>
      </div>
    </div>
  );
};

export const Subcomponent = ({
  selectedCellData,
}: {
  selectedCellData: any[];
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSubComponent, setSelectedSubComponent] = useState<string>('');
  const handleTreeCellClick = (event: any, name: string) => {
    setSelectedSubComponent((prev) => {
      if (prev !== name) {
        return name;
      } else {
        return prev;
      }
    });
  };

  const treeData = useMemo(() => {
    const tempSubComponentData = aggregateSubcomponentData(
      selectedCellData
    ).sort((a, b) => b.totalCost - a.totalCost);

    const minNonZeroValue = Math.min(
      ...tempSubComponentData.map((el) =>
        el?.totalCost > 0 ? el?.totalCost : Infinity
      )
    );

    // fallback to zero only if all zero else get a value less than min but greater than 0
    const minDisplayValue =
      minNonZeroValue === Infinity ? 0 : Math.ceil(minNonZeroValue / 10);

    const formattedWorkOrderData = tempSubComponentData.map((el) => ({
      ...el,
      totalCostForDisplay:
        el?.totalCost === 0 ? minDisplayValue : el?.totalCost,
    }));
    return formattedWorkOrderData;
  }, [selectedCellData]);

  useEffect(() => {
    if (treeData.length > 0) {
      handleTreeCellClick(null, treeData[0].name);
    }
    setIsLoading(false);
  }, [treeData]);

  // assets data of selectedSubComponent
  const { assetsData, subComponentData } = useMemo(() => {
    if (!selectedCellData.length)
      return { subComponentData: [], assetsData: [] };

    const newSelectedSubComponent =
      selectedSubComponent || selectedCellData[0].sub_component_categories;

    const filteredSubComponentData = selectedCellData.filter(
      (item) => item.sub_component_categories === newSelectedSubComponent
    );

    const aggregatedAssets = aggregateByAssetId(filteredSubComponentData);
    return {
      subComponentData: filteredSubComponentData,
      assetsData: aggregatedAssets,
    };
  }, [selectedCellData, selectedSubComponent]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="400px"
        width="100%"
      >
        <Typography
          sx={{
            fontSize: '20px',
            color: palette.textPrimary,
          }}
        >
          {t('cdp_loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <Box
        sx={{
          padding: '20px',
          backgroundColor: enableDarkTheme
            ? palette.toolBarBackground
            : palette.toolBarBackground,
          margin: 0,
        }}
        borderRadius={'8px'}
      >
        <Box>
          <ComponentsTreeChart
            selectedCell={selectedSubComponent}
            handleCellClick={handleTreeCellClick}
            data={treeData}
            enableDarkTheme={enableDarkTheme}
            open={false}
            anchorEl={''}
            handleClose={() => {}}
            handleShowTab={() => {}}
          />
        </Box>
      </Box>
      <DistributionComp
        assetsData={assetsData}
        selectedSubComponentData={subComponentData}
        selectedSubComponent={selectedSubComponent}
      />
    </Box>
  );
};
